import { Controller } from "@hotwired/stimulus"

const LABELS = { risk: "At risk", watch: "Watch", track: "On track", new: "New" }
const DIMENSIONS = { coach: "Weekly coach", team: "Team leader", year: "Year level", state: "State", subjectCoach: "Subject coach", time: "Time in program" }
const MULTI_CHIP_GROUPS = ["year", "state"]
const TIME_BUCKETS = [
  ["under_month", "Under 1 month", 1],
  ["one_to_three", "1 to 3 months", 3],
  ["three_to_six", "3 to 6 months", 6],
  ["six_to_twelve", "6 to 12 months", 12],
  ["over_year", "Over 1 year", Infinity]
]

export default class extends Controller {
  static targets = ["studentsBody", "count", "filterSummary", "activeFilters", "target", "stateTrend", "targetTrend", "bookedTrend", "headlinePercent", "headlineTarget", "statusText", "compareBody", "compareHeading", "compareBy", "attendedMetric", "bookedMetric", "showRateMetric", "weeklyOnlyMetric", "correctionsMetric", "aiCorrectionsMetric", "quietMetric", "riskHeading", "drawer", "drawerName", "drawerId", "drawerDetails", "drawerStatus", "drawerStatusChip", "drawerReason", "drawerChart", "drawerOnTarget", "drawerAttended", "drawerBooked", "drawerShowRate", "drawerCorrections", "drawerAiCorrections", "drawerLastCall", "backdrop", "pagination", "pageNumber", "tooltip", "helpPopover", "helpButton"]

  connect() {
    this.selectedStatus = "all"
    this.selectedReason = null
    this.studentSort = { key: "status", direction: 1 }
    this.compareSort = { key: "default", direction: -1 }
    this.selectedFilters = Object.fromEntries([...this.element.querySelectorAll("[data-dropdown-filter]")].map((dropdown) => [dropdown.dataset.dropdownFilter, []]))
    this.weekLabels = (this.element.dataset.weekLabels || "").split("|")
    this.stateSeries = JSON.parse(this.element.dataset.stateSeries || "{}")
    this.element.addEventListener("click", this.handleClick)
    this.element.addEventListener("change", this.handleChange)
    this.element.addEventListener("input", this.handleInput)
    this.element.addEventListener("pointermove", this.handlePointerMove)
    this.element.addEventListener("pointerdown", this.handlePointerDown)
    this.element.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this.handleOutsideClick)
    window.addEventListener("scroll", this.hideTooltip, { passive: true })
    this.applyFilters()
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleClick)
    this.element.removeEventListener("change", this.handleChange)
    this.element.removeEventListener("input", this.handleInput)
    this.element.removeEventListener("pointermove", this.handlePointerMove)
    this.element.removeEventListener("pointerdown", this.handlePointerDown)
    this.element.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleOutsideClick)
    window.removeEventListener("scroll", this.hideTooltip)
    if (this.boundResize) window.removeEventListener("resize", this.boundResize)
  }

  handleClick = (event) => {
    const themeToggle = event.target.closest("[data-theme-toggle]")
    if (themeToggle) {
      const dark = this.element.dataset.theme !== "dark"
      this.element.dataset.theme = dark ? "dark" : "light"
      themeToggle.setAttribute("aria-pressed", String(dark))
      return
    }
    const help = event.target.closest("[data-help-button]")
    if (help) {
      const open = this.helpPopoverTarget.hidden
      this.helpPopoverTarget.hidden = !open
      help.setAttribute("aria-expanded", String(open))
      if (open) this.positionHelpPopover()
      return
    }
    const trigger = event.target.closest("[data-dropdown-trigger]")
    if (trigger) {
      const dropdown = trigger.closest("[data-dropdown-filter]")
      const shouldOpen = dropdown.querySelector("[data-dropdown-menu]").hidden
      this.closeDropdowns()
      if (shouldOpen) {
        dropdown.querySelector("[data-dropdown-menu]").hidden = false
        trigger.setAttribute("aria-expanded", "true")
        dropdown.querySelector("[data-dropdown-search]")?.focus()
      }
      return
    }
    if (event.target.closest("[data-dropdown-done]")) { this.closeDropdowns(); return }
    const clear = event.target.closest("[data-dropdown-clear]")
    if (clear) {
      const dropdown = clear.closest("[data-dropdown-filter]")
      dropdown.querySelectorAll("[data-dropdown-option]").forEach((option) => { option.checked = false })
      this.selectedFilters[dropdown.dataset.dropdownFilter] = []
      this.refreshDropdownLabel(dropdown)
      this.applyFilters()
      return
    }
    const removeFilter = event.target.closest("[data-remove-active-filter]")
    if (removeFilter) { this.removeActiveFilter(removeFilter); return }
    const studentSort = event.target.closest("[data-student-sort]")
    if (studentSort) {
      const key = studentSort.dataset.studentSort
      this.studentSort = { key, direction: this.studentSort.key === key ? -this.studentSort.direction : (key === "name" || key === "state" || key === "coach" || key === "subjectCoach" ? 1 : -1) }
      this.currentPage = 1
      this.renderPage()
      return
    }
    const compareSort = event.target.closest("[data-compare-sort]")
    if (compareSort) {
      const key = compareSort.dataset.compareSort
      this.compareSort = { key, direction: this.compareSort.key === key ? -this.compareSort.direction : (key === "label" ? 1 : -1) }
      this.applyFilters()
      return
    }
    const reason = event.target.closest("[data-diagnosis-filter]")
    if (reason) {
      this.selectedReason = this.selectedReason === reason.dataset.diagnosisFilter ? null : reason.dataset.diagnosisFilter
      this.selectedStatus = this.selectedReason === "gone_quiet" ? "quiet" : this.selectedReason ? "risk" : "all"
      this.syncStatusTabs()
      this.applyFilters()
      return
    }
    const compareRow = event.target.closest("[data-compare-value]")
    if (compareRow) { this.selectCompareRow(compareRow); return }
    const pageButton = event.target.closest("[data-page]")
    if (pageButton) {
      const pages = Math.max(1, Math.ceil((this.currentRows || []).length / 25))
      this.currentPage = Math.min(pages, Math.max(1, (this.currentPage || 1) + (pageButton.dataset.page === "next" ? 1 : -1)))
      this.renderPage()
      return
    }
    if (event.target.closest("[data-student-row]")) { this.openDrawer(event); return }
    const button = event.target.closest("button")
    if (!button) return
    if (button.dataset.filterValue) {
      const group = button.closest("[data-filter-group]")
      if (!group) return
      const value = button.dataset.filterValue
      if (MULTI_CHIP_GROUPS.includes(group.dataset.filterGroup)) {
        const current = this.chipValues(group)
        this.setChipSelection(group, value === "All" ? [] : current.includes(value) ? current.filter((item) => item !== value) : [...current, value])
      } else this.setChipSelection(group, [value])
      this.applyFilters()
    } else if (button.dataset.statusTab) {
      this.selectedStatus = button.dataset.statusTab
      this.selectedReason = null
      this.syncStatusTabs()
      this.applyFilters()
    }
  }

  handleChange = (event) => {
    const option = event.target.closest("[data-dropdown-option]")
    if (option) {
      const dropdown = option.closest("[data-dropdown-filter]")
      this.selectedFilters[dropdown.dataset.dropdownFilter] = [...dropdown.querySelectorAll("[data-dropdown-option]:checked")].map((checked) => checked.value)
      this.refreshDropdownLabel(dropdown)
      this.applyFilters()
      return
    }
    if (event.target.matches("[data-dropdown-search]")) this.filterDropdownOptions(event.target)
    if (event.target.matches("[data-compare-by]")) {
      this.compareSort = { key: "default", direction: -1 }
      this.applyFilters()
    }
  }

  handleInput = (event) => {
    if (event.target.matches("[data-dropdown-search]")) this.filterDropdownOptions(event.target)
    if (event.target.matches("[data-student-search]")) this.applyFilters()
  }

  handleOutsideClick = (event) => {
    if (!event.target.closest("[data-dropdown-filter]")) this.closeDropdowns()
    if (!event.target.closest("[data-help-wrap]")) this.closeHelpPopover()
  }

  handlePointerMove = (event) => {
    const trigger = event.target.closest?.("[data-tip]")
    if (!trigger) { this.hideTooltip(); return }
    this.showTooltip(trigger.dataset.tip, event.clientX, event.clientY)
  }

  handlePointerDown = (event) => {
    const trigger = event.target.closest?.("[data-tip]")
    if (trigger && event.pointerType !== "mouse") this.showTooltip(trigger.dataset.tip, event.clientX, event.clientY)
    else if (!trigger) this.hideTooltip()
  }

  handleKeydown = (event) => {
    const activatedRow = event.target.closest?.("[data-student-row], [data-compare-value]")
    if (activatedRow && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      activatedRow.click()
      return
    }
    if (event.key !== "Escape") return
    if (!this.helpPopoverTarget.hidden) {
      this.closeHelpPopover()
      this.helpButtonTarget.focus()
      return
    }
    const openDropdown = [...this.element.querySelectorAll("[data-dropdown-menu]")].find((menu) => !menu.hidden)
    if (openDropdown) {
      const trigger = openDropdown.closest("[data-dropdown-filter]").querySelector("[data-dropdown-trigger]")
      this.closeDropdowns()
      trigger.focus()
      return
    }
    if (this.drawerTarget.classList.contains("is-open")) this.closeDrawer()
  }

  showTooltip(text, x, y) {
    if (!text || !this.hasTooltipTarget) return
    const tooltip = this.tooltipTarget
    tooltip.textContent = text
    tooltip.hidden = false
    const rect = tooltip.getBoundingClientRect()
    let left = x + 14
    let top = y + 16
    if (left + rect.width > window.innerWidth - 8) left = x - rect.width - 14
    if (top + rect.height > window.innerHeight - 8) top = y - rect.height - 14
    tooltip.style.left = `${Math.max(8, left)}px`
    tooltip.style.top = `${Math.max(8, top)}px`
  }

  hideTooltip = () => { if (this.hasTooltipTarget) this.tooltipTarget.hidden = true }

  positionHelpPopover() {
    const popover = this.helpPopoverTarget
    popover.style.transform = ""
    const rect = popover.getBoundingClientRect()
    let shift = 0
    if (rect.left < 8) shift = 8 - rect.left
    else if (rect.right > window.innerWidth - 8) shift = window.innerWidth - 8 - rect.right
    if (shift) popover.style.transform = `translateX(calc(-50% + ${Math.round(shift)}px))`
  }

  closeHelpPopover() {
    if (!this.hasHelpPopoverTarget) return
    this.helpPopoverTarget.hidden = true
    if (this.hasHelpButtonTarget) this.helpButtonTarget.setAttribute("aria-expanded", "false")
  }

  chipValues(group) {
    return [...group.querySelectorAll(".is-selected")].map((button) => button.dataset.filterValue).filter((value) => value !== "All")
  }

  setChipSelection(group, values) {
    group.querySelectorAll("button").forEach((item) => {
      const selected = values.length ? values.includes(item.dataset.filterValue) : item.dataset.filterValue === "All"
      item.classList.toggle("is-selected", selected)
      item.setAttribute("aria-pressed", selected)
    })
  }

  weekFlags(row) {
    const weekCount = (row.dataset.weeks || "").split(",").length
    const holidays = (row.dataset.holidayWeeks || "").split(",").map((value) => value === "1")
    const firstProgramIndex = weekCount - Math.min(Number(row.dataset.programWeeks || 0), weekCount)
    return Array.from({ length: weekCount }, (_, index) => ({ started: index >= firstProgramIndex, holiday: holidays[index] }))
  }

  syncStatusTabs() {
    this.element.querySelectorAll("[data-status-tab]").forEach((tab) => tab.classList.toggle("is-active", tab.dataset.statusTab === this.selectedStatus))
  }

  closeDropdowns() {
    this.element.querySelectorAll("[data-dropdown-filter]").forEach((dropdown) => {
      dropdown.querySelector("[data-dropdown-menu]").hidden = true
      dropdown.querySelector("[data-dropdown-trigger]").setAttribute("aria-expanded", "false")
    })
  }

  refreshDropdownLabel(dropdown) {
    const selected = this.selectedFilters[dropdown.dataset.dropdownFilter] || []
    const label = selected.length === 0 ? "All" : selected.length === 1 ? dropdown.querySelector(`[data-dropdown-option][value="${CSS.escape(selected[0])}"]`)?.closest(".select-option")?.querySelector("span")?.textContent || selected[0] : `${selected.length} selected`
    dropdown.querySelector("[data-dropdown-label]").textContent = label
    dropdown.querySelector("[data-dropdown-trigger]").classList.toggle("has-selection", selected.length > 0)
  }

  filterDropdownOptions(search) {
    const query = search.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
    search.closest("[data-dropdown-filter]").querySelectorAll(".select-option").forEach((option) => {
      const words = option.dataset.optionName.split(/\s+/)
      option.hidden = !query.every((term) => words.some((word) => word.startsWith(term)))
    })
  }

  timeBucket(row) {
    const elapsedMonths = (new Date(this.element.dataset.reportingThrough) - new Date(row.dataset.programStart)) / (1000 * 60 * 60 * 24 * 30.4375)
    if (elapsedMonths < 1) return "under_month"
    if (elapsedMonths < 3) return "one_to_three"
    if (elapsedMonths < 6) return "three_to_six"
    if (elapsedMonths < 12) return "six_to_twelve"
    return "over_year"
  }

  programWeekIndices(row, weekCount, windowWeeks) {
    const holidays = (row.dataset.holidayWeeks || "").split(",").map((value) => value === "1")
    const firstProgramIndex = weekCount - Math.min(Number(row.dataset.programWeeks || 0), weekCount)
    return Array.from({ length: weekCount }, (_, index) => index)
      .filter((index) => index >= firstProgramIndex && !holidays[index]).slice(-windowWeeks)
  }

  windowSummary(row, windowWeeks) {
    const attended = (row.dataset.weeks || "").split(",").map(Number)
    const booked = (row.dataset.bookedWeeks || "").split(",").map(Number)
    const indices = this.programWeekIndices(row, attended.length, windowWeeks)
    const sum = (values) => indices.reduce((total, index) => total + Number(values?.[index] || 0), 0)
    return { indices, weeks: indices.length, attended: sum(attended), booked: sum(booked),
      corrections: sum((row.dataset.correctionsWeeks || "").split(",")),
      aiCorrections: sum((row.dataset.aiCorrectionsWeeks || "").split(",")), sum }
  }

  renderStudentWeeks(row, attendedWeeks, bookedWeeks, target, windowWeeks) {
    const container = row.querySelector(".week-cells")
    if (!container) return
    const labels = (row.dataset.weekLabels || "").split("|")
    const indices = this.programWeekIndices(row, attendedWeeks.length, windowWeeks)
    container.replaceChildren(...indices.map((index) => {
      const attended = attendedWeeks[index], booked = bookedWeeks[index]
      const cell = document.createElement("span")
      cell.className = `cell ${attended >= target ? "hit" : attended > 0 ? "part" : "zero"}${booked > attended ? " miss" : ""}`
      cell.textContent = String(attended)
      const tip = `Week of ${labels[index]}: attended ${attended} of ${booked} calls`
      cell.dataset.tip = tip
      cell.setAttribute("aria-label", tip)
      return cell
    }))
  }

  statusFor(row, attendedWeeks, bookedWeeks, target, windowWeeks) {
    const activeWeeks = Number(row.dataset.activeWeeks || 0)
    if (activeWeeks < 3) return { status: "new", quiet: false, reason: null, hitRate: 0 }
    const eligibleIndices = this.programWeekIndices(row, attendedWeeks.length, windowWeeks)
    if (!eligibleIndices.length) return { status: "new", quiet: false, reason: null, hitRate: 0 }
    const recentAttended = eligibleIndices.map((index) => attendedWeeks[index])
    const eligibleWeeks = Math.min(windowWeeks, activeWeeks, recentAttended.length)
    const targetWeeks = recentAttended.filter((count) => count >= target).length
    const hitRate = targetWeeks / Math.max(eligibleWeeks, 1)
    const quiet = eligibleIndices.length >= 2 && eligibleIndices.slice(-2).reduce((sum, index) => sum + attendedWeeks[index], 0) === 0
    if (quiet) return { status: "risk", quiet: true, reason: "gone_quiet", hitRate }
    if (targetWeeks >= Math.ceil(eligibleWeeks * 0.75)) return { status: "track", quiet: false, reason: null, hitRate }
    const shortWeeks = eligibleIndices.filter((index) => attendedWeeks[index] < target)
    const underbookedWeeks = shortWeeks.filter((index) => bookedWeeks[index] < target).length
    const reason = underbookedWeeks >= shortWeeks.length - underbookedWeeks ? "underbooking" : "no_shows"
    return { status: targetWeeks >= Math.ceil(eligibleWeeks * 0.5) ? "watch" : "risk", quiet: false, reason, hitRate }
  }

  applyFilters() {
    if (!this.hasStudentsBodyTarget) return
    const filters = Object.fromEntries([...this.element.querySelectorAll("[data-filter-group]")].map((group) => [group.dataset.filterGroup, this.chipValues(group)]))
    const target = Number(filters.target[0] || 2)
    const windowWeeks = Number(filters.weeks[0] || 4)
    this.reportingWindowWeeks = windowWeeks
    this.element.querySelectorAll("[data-reporting-window-label]").forEach((label) => { label.textContent = `last ${windowWeeks} program weeks` })
    const weeksHeading = this.element.querySelector("[data-student-weeks-heading]")
    if (weeksHeading) weeksHeading.textContent = `Last ${windowWeeks} program weeks`
    this.targetTarget.textContent = String(target)
    this.headlineTargetTarget.textContent = String(target)
    // Read the controls as the source of truth each time. This keeps every
    // analytics section in sync even if a dropdown was changed by keyboard or
    // restored by the browser before the controller connected.
    const dropdowns = Object.fromEntries([...this.element.querySelectorAll("[data-dropdown-filter]")].map((dropdown) => [
      dropdown.dataset.dropdownFilter,
      [...dropdown.querySelectorAll("[data-dropdown-option]:checked")].map((option) => option.value)
    ]))
    const coachDropdown = this.element.querySelector('[data-dropdown-filter="coach"]')
    if (coachDropdown) {
      coachDropdown.querySelectorAll("[data-dropdown-option]").forEach((option) => {
        const teams = (option.closest(".select-option").dataset.optionTeam || "").split("|")
        const off = dropdowns.team.length > 0 && !teams.some((team) => dropdowns.team.includes(team))
        option.closest(".select-option").classList.toggle("is-off", off)
        if (off) option.checked = false
      })
      dropdowns.coach = dropdowns.coach.filter((value) => coachDropdown.querySelector(`[data-dropdown-option][value="${CSS.escape(value)}"]`)?.checked)
    }
    this.selectedFilters = dropdowns
    if (coachDropdown) this.refreshDropdownLabel(coachDropdown)
    const query = this.element.querySelector("[data-student-search]")?.value.trim().toLocaleLowerCase() || ""
    const rows = [...this.studentsBodyTarget.querySelectorAll("[data-student-row]")]
    rows.forEach((row) => {
      const attendedWeeks = (row.dataset.weeks || "").split(",").map(Number)
      const bookedWeeks = (row.dataset.bookedWeeks || "").split(",").map(Number)
      this.renderStudentWeeks(row, attendedWeeks, bookedWeeks, target, windowWeeks)
      const summary = this.windowSummary(row, windowWeeks)
      row.dataset.attendedAverage = String(summary.attended / Math.max(summary.weeks, 1))
      row.dataset.bookedAverage = String(summary.booked / Math.max(summary.weeks, 1))
      row.dataset.showRate = String(summary.booked ? summary.attended / summary.booked * 100 : 0)
      row.children[6].textContent = Number(row.dataset.attendedAverage).toFixed(1)
      row.children[7].textContent = Number(row.dataset.bookedAverage).toFixed(1)
      row.children[8].textContent = summary.booked ? `${Math.round(Number(row.dataset.showRate))}%` : "—"
      row.children[9].textContent = String(summary.corrections)
      row.children[10].textContent = String(summary.aiCorrections)
      const state = this.statusFor(row, attendedWeeks, bookedWeeks, target, windowWeeks)
      row.dataset.status = state.status
      row.dataset.quiet = String(state.quiet)
      row.dataset.reason = state.reason || ""
      row.dataset.hitRate = String(state.hitRate)
      const pill = row.querySelector(".status-pill")
      pill.className = `status-pill status-${state.quiet ? "risk" : state.status}`
      pill.textContent = state.quiet ? "Gone quiet" : LABELS[state.status]
    })
    const filteredRows = rows.filter((row) => {
      const yearMatch = !filters.year.length || filters.year.includes(row.dataset.year)
      const stateMatch = !filters.state.length || filters.state.includes(row.dataset.state)
      const coachMatch = !dropdowns.coach?.length || dropdowns.coach.includes(row.dataset.coach)
      const teamMatch = !dropdowns.team?.length || dropdowns.team.includes(row.dataset.team)
      const subjectCoaches = row.dataset.subjectCoach === "—" ? ["Unassigned"] : row.dataset.subjectCoach.split(", ")
      const subjectMatch = !dropdowns.subjectCoach?.length || dropdowns.subjectCoach.some((name) => subjectCoaches.includes(name))
      const timeMatch = !dropdowns.time?.length || dropdowns.time.includes(this.timeBucket(row))
      return yearMatch && stateMatch && coachMatch && teamMatch && subjectMatch && timeMatch
    })
    const visible = filteredRows.filter((row) => {
      const statusMatch = this.selectedStatus === "all" || (this.selectedStatus === "risk" && row.dataset.status === "risk") || (this.selectedStatus === "quiet" && row.dataset.quiet === "true") || row.dataset.status === this.selectedStatus
      const nameMatch = !query || `${row.dataset.name} ${row.dataset.studentId}`.toLocaleLowerCase().includes(query)
      return statusMatch && (!this.selectedReason || row.dataset.reason === this.selectedReason) && nameMatch
    })
    this.currentRows = visible
    this.currentPage = 1
    this.renderPage()
    this.renderCompare(filteredRows)
    this.renderMetrics(filteredRows, target, windowWeeks)
    this.renderTrends(filteredRows, filters.state.length === 1 ? filters.state[0] : "All", target)
    this.countTarget.textContent = `${filteredRows.length} of ${rows.length} students`
    this.renderActiveFilters(filters, dropdowns, filteredRows.length, rows.length)
    this.updateStatus(filteredRows, target, windowWeeks)
  }

  renderActiveFilters(filters, dropdowns, filteredCount, allCount) {
    const tags = []
    filters.year.forEach((value) => tags.push({ label: `Year ${value}`, group: "year", value }))
    filters.state.forEach((value) => tags.push({ label: value, group: "state", value }))
    const labels = { team: "Team leader", coach: "Weekly coach", subjectCoach: "Subject coach", time: "Time in program" }
    Object.entries(dropdowns).forEach(([group, values]) => {
      (values || []).forEach((value) => {
        const option = this.element.querySelector(`[data-dropdown-filter="${group}"] [data-dropdown-option][value="${CSS.escape(value)}"]`)
        const optionLabel = option?.closest(".select-option")?.querySelector("span")?.textContent?.trim() || value
        tags.push({ label: `${labels[group] || group}: ${optionLabel}`, group, value })
      })
    })
    this.activeFiltersTarget.replaceChildren()
    if (!tags.length) {
      const empty = document.createElement("span")
      empty.className = "filter-empty"
      empty.textContent = "No filters on. Showing every student."
      this.activeFiltersTarget.appendChild(empty)
      return
    }
    tags.forEach(({ label, group, value }) => {
      const tag = document.createElement("button")
      tag.type = "button"
      tag.className = "afilter"
      tag.dataset.removeActiveFilter = ""
      tag.dataset.removeFilterGroup = group
      tag.dataset.removeFilterValue = value
      tag.setAttribute("aria-label", `Remove ${label}`)
      tag.append(document.createTextNode(label))
      const close = document.createElement("span")
      close.setAttribute("aria-hidden", "true")
      close.textContent = "×"
      tag.appendChild(close)
      this.activeFiltersTarget.appendChild(tag)
    })
    const count = document.createElement("span")
    count.className = "filter-result-count"
    count.textContent = `${filteredCount} of ${allCount} students`
    this.activeFiltersTarget.appendChild(count)
  }

  removeActiveFilter(button) {
    const { removeFilterGroup: groupName, removeFilterValue: value } = button.dataset
    const group = this.element.querySelector(`[data-filter-group="${groupName}"]`)
    if (group) this.setChipSelection(group, this.chipValues(group).filter((item) => item !== value))
    else {
      const dropdown = this.element.querySelector(`[data-dropdown-filter="${groupName}"]`)
      if (!dropdown) return
      const option = dropdown.querySelector(`[data-dropdown-option][value="${CSS.escape(value)}"]`)
      if (option) option.checked = false
      this.selectedFilters[groupName] = (this.selectedFilters[groupName] || []).filter((item) => item !== value)
      this.refreshDropdownLabel(dropdown)
    }
    this.applyFilters()
  }

  compareStudentRows(a, b) {
    const key = this.studentSort.key
    const values = {
      name: [a.dataset.name, b.dataset.name], year: [Number(a.dataset.year) || 0, Number(b.dataset.year) || 0],
      state: [a.dataset.state, b.dataset.state], coach: [a.dataset.coach, b.dataset.coach], subjectCoach: [a.dataset.subjectCoachLabel, b.dataset.subjectCoachLabel],
      attended: [Number(a.dataset.attendedAverage) || 0, Number(b.dataset.attendedAverage) || 0], booked: [Number(a.dataset.bookedAverage) || 0, Number(b.dataset.bookedAverage) || 0],
      showRate: [Number(a.dataset.showRate) || 0, Number(b.dataset.showRate) || 0], corrections: [this.windowSummary(a, this.reportingWindowWeeks).corrections, this.windowSummary(b, this.reportingWindowWeeks).corrections],
      aiCorrections: [this.windowSummary(a, this.reportingWindowWeeks).aiCorrections, this.windowSummary(b, this.reportingWindowWeeks).aiCorrections], lastCall: [Date.parse(a.dataset.lastCallAt) || 0, Date.parse(b.dataset.lastCallAt) || 0]
    }
    if (key === "status") {
      const order = { risk: 0, watch: 1, new: 2, track: 3 }
      const rank = (row) => [(order[row.dataset.status] ?? 4) - (row.dataset.quiet === "true" ? 0.5 : 0), Number(row.dataset.hitRate) || 0, Number(row.dataset.attendedAverage) || 0]
      const [left, right] = [rank(a), rank(b)]
      return ((left[0] - right[0]) || (left[1] - right[1]) || (left[2] - right[2])) * this.studentSort.direction || a.dataset.name.localeCompare(b.dataset.name)
    }
    const [left, right] = values[key] || [0, 0]
    const result = typeof left === "string" ? left.localeCompare(right, undefined, { numeric: true }) : left - right
    return result * this.studentSort.direction || a.dataset.name.localeCompare(b.dataset.name)
  }

  updateStatus(rows, target, windowWeeks) {
    const counts = { risk: 0, watch: 0, track: 0, new: 0, quiet: 0 }
    rows.forEach((row) => {
      const status = row.dataset.status
      if (["risk", "watch", "track", "new"].includes(status)) counts[status] += 1
      if (row.dataset.quiet === "true") counts.quiet += 1
    })
    Object.entries(counts).forEach(([status, count]) => {
      this.element.querySelectorAll(`[data-status-count="${status}"], [data-tab-count="${status}"]`).forEach((node) => { node.textContent = String(count) })
    })
    this.element.querySelectorAll('[data-tab-count="all"]').forEach((node) => { node.textContent = String(rows.length) })
    const totals = { risk: counts.risk, watch: counts.watch, track: counts.track, new: counts.new }
    Object.entries(totals).forEach(([status, count]) => {
      const segment = this.element.querySelector(`.${status}-segment`)
      if (segment) segment.style.flex = String(count)
    })
    const reasons = { underbooking: 0, no_shows: 0, gone_quiet: 0 }
    rows.forEach((row) => {
      if (row.dataset.status === "risk" && Object.hasOwn(reasons, row.dataset.reason)) reasons[row.dataset.reason] += 1
    })
    Object.entries(reasons).forEach(([name, count]) => {
      const share = counts.risk ? count / counts.risk : 0
      const countNode = this.element.querySelector(`[data-reason-count="${name}"]`)
      const barNode = this.element.querySelector(`[data-reason-bar="${name}"]`)
      if (countNode) countNode.textContent = `${count} (${Math.round(share * 100)}%)`
      if (barNode) barNode.style.width = `${Math.round(share * 100)}%`
    })
    this.riskHeadingTarget.textContent = `Why ${counts.risk} students are at risk`
    this.statusTextTarget.textContent = `Status based on the last ${windowWeeks} non-holiday program weeks`
  }

  renderMetrics(rows, target, windowWeeks) {
    const count = rows.length
    const summaries = rows.map((row) => this.windowSummary(row, windowWeeks))
    const exposure = Math.max(summaries.reduce((sum, data) => sum + data.weeks, 0), 1)
    const attended = summaries.reduce((sum, data) => sum + data.attended, 0)
    const booked = summaries.reduce((sum, data) => sum + data.booked, 0)
    this.attendedMetricTarget.textContent = (attended / exposure).toFixed(2)
    this.bookedMetricTarget.textContent = (booked / exposure).toFixed(2)
    this.showRateMetricTarget.textContent = `${Math.round(booked ? attended / booked * 100 : 0)}%`
    const correctionTotals = summaries.reduce((totals, data) => ({ corrections: totals.corrections + data.corrections, aiCorrections: totals.aiCorrections + data.aiCorrections }), { corrections: 0, aiCorrections: 0 })
    this.correctionsMetricTarget.textContent = (correctionTotals.corrections / exposure).toFixed(2)
    this.aiCorrectionsMetricTarget.textContent = (correctionTotals.aiCorrections / exposure).toFixed(2)
    const evaluatedRows = rows.filter((row) => row.dataset.status !== "new")
    const weeklyOnly = evaluatedRows.filter((row) => {
      const series = JSON.parse(row.dataset.typeSeries || "{}")
      const { sum, attended: totalAttended } = this.windowSummary(row, windowWeeks)
      const other = ["subject_call", "strategy_call"].reduce((total, type) => total + sum(series[type]?.attended), 0)
      return totalAttended > 0 && other === 0
    }).length
    this.weeklyOnlyMetricTarget.textContent = evaluatedRows.length ? `${Math.round(weeklyOnly / evaluatedRows.length * 100)}%` : "—"
    this.quietMetricTarget.textContent = String(rows.filter((row) => row.dataset.quiet === "true").length)
    const byType = { weekly_call: { booked: 0, attended: 0 }, subject_call: { booked: 0, attended: 0 }, strategy_call: { booked: 0, attended: 0 } }
    rows.forEach((row) => {
      const series = JSON.parse(row.dataset.typeSeries || "{}")
      const { sum } = this.windowSummary(row, windowWeeks)
      Object.keys(byType).forEach((type) => {
        byType[type].booked += sum(series[type]?.booked)
        byType[type].attended += sum(series[type]?.attended)
      })
    })
    const typeAverages = Object.values(byType).map((totals) => totals.booked / exposure)
    const maxTypeAverage = Math.max(1.2, ...typeAverages)
    Object.entries(byType).forEach(([type, totals]) => {
      const card = this.element.querySelector(`[data-call-metric="${type}"]`)
      const avgBooked = totals.booked / exposure
      const avgAttended = totals.attended / exposure
      const rate = totals.booked ? totals.attended / totals.booked : 0
      card.querySelector("[data-call-attended]").textContent = avgAttended.toFixed(2)
      card.querySelector("[data-call-booked]").textContent = avgBooked.toFixed(2)
      card.querySelector("[data-call-show-rate]").textContent = `${Math.round(rate * 100)}%`
      card.querySelector("[data-call-booked-bar]").style.width = `${Math.min(100, avgBooked / maxTypeAverage * 100)}%`
      card.querySelector("[data-call-attended-bar]").style.width = `${Math.min(100, avgAttended / maxTypeAverage * 100)}%`
    })
    const maxCorrectionAverage = Math.max(0.2, correctionTotals.corrections / exposure, correctionTotals.aiCorrections / exposure);
    [["corrections", correctionTotals.corrections], ["ai_corrections", correctionTotals.aiCorrections]].forEach(([type, total]) => {
      const card = this.element.querySelector(`[data-correction-metric="${type}"]`)
      const average = total / exposure
      card.querySelector("[data-correction-average]").textContent = average.toFixed(2)
      card.querySelector("[data-correction-bar]").style.width = `${Math.min(100, average / maxCorrectionAverage * 100)}%`
    })
  }

  renderTrends(rows, state, target) {
    const windowWeeks = this.reportingWindowWeeks || 4
    const summaries = rows.map((row) => this.windowSummary(row, windowWeeks))
    this.stateTrendTarget.hidden = state === "All"
    const weekCount = this.weekLabels.length
    const flags = rows.map((row) => this.weekFlags(row))
    const values = this.weekLabels.map((label, index) => {
      const started = rows.filter((_, rowIndex) => flags[rowIndex][index]?.started)
      const members = rows.filter((_, rowIndex) => flags[rowIndex][index]?.started && !flags[rowIndex][index]?.holiday)
      const holiday = started.length > 0 && members.length < started.length / 2
      const total = (field) => members.reduce((sum, row) => sum + Number((row.dataset[field] || "").split(",")[index] || 0), 0)
      return { label, booked: total("bookedWeeks") / Math.max(members.length, 1), attended: total("weeks") / Math.max(members.length, 1), corrections: total("correctionsWeeks"), aiCorrections: total("aiCorrectionsWeeks"), holiday, skip: holiday || !members.length }
    })
    const chart = (width, height, includeTarget = true) => {
      const narrow = width < 620
      const left = 40, right = 8, top = 18, bottom = 28
      const plotWidth = width - left - right, plotHeight = height - top - bottom
      const peak = Math.max(3, target, ...values.flatMap((point) => [point.booked, point.attended]))
      const maxY = Math.max(3, Math.ceil(peak))
      const x = (index) => left + (index + 0.5) * plotWidth / weekCount
      const y = (value) => top + (1 - value / maxY) * plotHeight
      const holidays = []
      let run = null
      values.forEach((point, index) => {
        if (point.holiday && run === null) run = index
        if ((!point.holiday || index === weekCount - 1) && run !== null) {
          const end = point.holiday && index === weekCount - 1 ? index + 1 : index
          holidays.push(`<rect class="trend-holiday" x="${left + run * plotWidth / weekCount}" y="${top}" width="${(end - run) * plotWidth / weekCount}" height="${plotHeight}"/>`)
          run = null
        }
      })
      const pattern = `<defs><pattern id="trend-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="8" height="8" class="chart-hatch-paper"/><line x1="0" y1="0" x2="0" y2="8" class="chart-hatch-line" stroke-width="2"/></pattern></defs>`
      const grid = Array.from({ length: maxY + 1 }, (_, value) => `<line class="trend-grid ${value === 0 ? "is-base" : ""}" x1="${left}" x2="${width - right}" y1="${y(value)}" y2="${y(value)}"/>${maxY <= 6 || value % 2 === 0 ? `<text class="trend-axis-label" x="${left - 8}" y="${y(value) + 4}" text-anchor="end">${value}</text>` : ""}`).join("")
      const pathFor = (field) => values.map((point, index) => point.skip ? null : `${index === 0 || values[index - 1]?.skip ? "M" : "L"}${x(index).toFixed(1)} ${y(point[field]).toFixed(1)}`).filter(Boolean).join(" ")
      const ticks = values.map((point, index) => (!narrow || (weekCount - 1 - index) % 2 === 0) ? `<text class="trend-axis-label" x="${x(index)}" y="${height - 5}" text-anchor="middle">${point.label}</text>` : "").join("")
      const targetLine = includeTarget ? `<line class="trend-target" x1="${left}" x2="${width - right}" y1="${y(target)}" y2="${y(target)}"/><text class="trend-target-label" x="${width - right - 2}" y="${y(target) - 5}" text-anchor="end">Target ${target}</text>` : ""
      const points = values.map((point, index) => point.skip ? "" : `<circle class="trend-dot-booked" cx="${x(index)}" cy="${y(point.booked)}" r="3"/><circle class="trend-dot-attended" cx="${x(index)}" cy="${y(point.attended)}" r="4"/><rect class="trend-hit" x="${left + index * plotWidth / weekCount}" y="${top}" width="${plotWidth / weekCount}" height="${plotHeight}" data-tip="${point.label}: ${point.booked.toFixed(2)} booked, ${point.attended.toFixed(2)} attended per student; ${point.corrections} Corrections; ${point.aiCorrections} AI corrections"/>`).join("")
      return `<svg class="trend-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="Booked and attended calls per student for the last 12 weeks">${pattern}${grid}${holidays.join("")}${targetLine}<path class="trend-line-booked" d="${pathFor("booked")}"/><path class="trend-line-attended" d="${pathFor("attended")}"/>${points}${ticks}</svg>`
    }
    const renderBookingTrend = () => {
      const width = Math.max(300, this.bookedTrendTarget.clientWidth || this.bookedTrendTarget.parentElement?.clientWidth || 300)
      this.bookedTrendTarget.innerHTML = chart(width, 232)
    }
    this.renderBookingTrend = renderBookingTrend
    renderBookingTrend()
    if (!this.boundResize) { this.boundResize = () => this.renderBookingTrend?.(); window.addEventListener("resize", this.boundResize) }
    if (state !== "All") {
      const stateData = this.stateSeries[state] || []
      const labels = this.weekLabels
      const weekCount = labels.length
      const holidayRuns = []
      let runStart = null
      stateData.forEach((week, index) => {
        if (week?.holiday && runStart === null) runStart = index
        if ((!week?.holiday || index === stateData.length - 1) && runStart !== null) {
          const runEnd = week?.holiday && index === stateData.length - 1 ? index : index - 1
          holidayRuns.push({ start: runStart, length: runEnd - runStart + 1 })
          runStart = null
        }
      })
      const holidayBlocks = holidayRuns.map(({ start, length }) => {
        const end = start + length
        const trailingGap = end < weekCount ? 48 : 0
        return `<div class="holiday-range" style="left:${start / weekCount * 100}%;width:calc(${length / weekCount * 100}% - ${trailingGap}px)" role="img" aria-label="School holidays from ${labels[start]} through ${labels[end - 1]}"><span>School<br>holidays</span></div>`
      }).join("")
      const weekSlots = labels.map((label, slot) => {
        const inProgram = rows.filter((row) => slot >= weekCount - Math.min(Number(row.dataset.programWeeks || 0), weekCount))
        const targetRows = inProgram.filter((row) => Number((row.dataset.weeks || "").split(",")[slot] || 0) >= target).length
        const share = inProgram.length ? Math.round(targetRows / inProgram.length * 100) : 0
        const holiday = Boolean(stateData[slot]?.holiday)
        const tip = holiday ? `School holidays · ${label}` : `${share}% of students reached the target · ${label}`
        const barShare = share * 0.7
        return `<div class="trend-bar-wrap ${holiday ? "is-holiday-slot" : ""} ${slot === weekCount - 1 ? "is-latest" : ""}" style="--bar-height:${barShare}%" data-tip="${tip}">${holiday ? "" : `<div class="trend-bar target-share-bar" style="height:${barShare}%"></div><b>${share}%</b>`}<small>${label}</small></div>`
      }).join("")
      this.targetTrendTarget.innerHTML = `<span class="target-axis axis-top">100%</span><span class="target-axis axis-middle">50%</span><span class="target-axis axis-bottom">0%</span><div class="target-chart-plot">${holidayBlocks}${weekSlots}</div>`
    }
    const reached = summaries.reduce((sum, data, rowIndex) => sum + data.indices.filter((index) => Number(rows[rowIndex].dataset.weeks.split(",")[index]) >= target).length, 0)
    const totalWeeks = summaries.reduce((sum, data) => sum + data.weeks, 0)
    this.headlinePercentTarget.textContent = `${Math.round(reached / Math.max(totalWeeks, 1) * 100)}%`
  }

  renderCompare(rows) {
    const dimension = this.compareByTarget.value
    const heading = DIMENSIONS[dimension] || "Weekly coach"
    this.compareHeadingTarget.textContent = heading
    const groups = new Map()
    rows.forEach((row) => {
      let values = []
      if (dimension === "subjectCoach") values = row.dataset.subjectCoach.split(", ").filter((name) => name && name !== "—")
      else if (dimension === "time") values = [TIME_BUCKETS.find(([, , upper]) => {
        const months = (new Date(this.element.dataset.reportingThrough) - new Date(row.dataset.programStart)) / (1000 * 60 * 60 * 24 * 30.4375)
        return months < upper
      })?.[0] || "over_year"]
      else values = [row.dataset[dimension === "coach" ? "coach" : dimension] || "Unassigned"]
      if (!values.length) values = ["Unassigned"]
      values.forEach((value) => { if (!groups.has(value)) groups.set(value, []); groups.get(value).push(row) })
    })
    const metrics = (members) => {
      const count = members.length
      const summaries = members.map((row) => this.windowSummary(row, this.reportingWindowWeeks || 4))
      const exposure = Math.max(summaries.reduce((sum, data) => sum + data.weeks, 0), 1)
      const attended = summaries.reduce((sum, data) => sum + data.attended, 0) / exposure
      const booked = summaries.reduce((sum, data) => sum + data.booked, 0) / exposure
      const corrections = summaries.reduce((sum, data) => sum + data.corrections, 0) / exposure
      const aiCorrections = summaries.reduce((sum, data) => sum + data.aiCorrections, 0) / exposure
      const evaluated = members.filter((row) => row.dataset.status !== "new").length
      const onTrack = members.filter((row) => row.dataset.status === "track").length / Math.max(evaluated, 1)
      const atRisk = members.filter((row) => row.dataset.status === "risk").length / Math.max(evaluated, 1)
      return { count, evaluated, attended, booked, corrections, aiCorrections, showRate: booked ? attended / booked : 0, onTrack, atRisk }
    }
    const overall = metrics(rows)
    const isHot = (data) => data.evaluated >= 10 && data.atRisk >= overall.atRisk + 0.08
    const isSmall = (data) => data.evaluated < 10
    const tier = (data) => isHot(data) ? 0 : isSmall(data) ? 2 : 1
    const buildRow = (name, data, all = false) => {
      const row = document.createElement("tr")
      row.className = all ? "overall-row" : isHot(data) ? "compare-at-risk" : ""
      if (!all) { row.dataset.compareValue = name; row.dataset.compareDimension = dimension; row.tabIndex = 0 }
      const label = dimension === "time" ? TIME_BUCKETS.find(([value]) => value === name)?.[1] || name : name
      const values = [all ? "Everyone in view" : label, data.count, data.attended.toFixed(1), data.booked.toFixed(1), `${Math.round(data.showRate * 100)}%`, `${Math.round(data.onTrack * 100)}%`]
      values.forEach((value, index) => { const cell = document.createElement(index === 0 ? "th" : "td"); cell.textContent = String(value); row.appendChild(cell) })
      if (!all && isSmall(data)) {
        const tag = document.createElement("span")
        tag.className = "compare-small-tag"
        tag.textContent = "small group"
        row.firstChild.appendChild(tag)
      }
      const cell = document.createElement("td")
      const meter = document.createElement("div")
      meter.className = "compare-risk-meter"
      const bar = document.createElement("i")
      const risk = Math.round(data.atRisk * 100)
      bar.style.setProperty("--risk-share", `${risk}%`)
      const text = document.createElement("span")
      text.textContent = `${risk}%`
      meter.append(bar, text)
      cell.appendChild(meter)
      row.appendChild(cell)
      ;[data.corrections.toFixed(2), data.aiCorrections.toFixed(2)].forEach((value) => {
        const metricCell = document.createElement("td")
        metricCell.textContent = value
        row.appendChild(metricCell)
      })
      return row
    }
    this.element.querySelectorAll("[data-compare-sort]").forEach((button) => {
      const active = button.dataset.compareSort === this.compareSort.key
      button.setAttribute("aria-sort", active ? (this.compareSort.direction === 1 ? "ascending" : "descending") : "none")
    })
    const sortValue = (data) => ({ count: data.count, attended: data.attended, booked: data.booked, showRate: data.showRate, onTrack: data.onTrack, atRisk: data.atRisk, corrections: data.corrections, aiCorrections: data.aiCorrections })[this.compareSort.key]
    const naturalOrder = { year: (name) => Number(name) || 99, state: (name) => ["VIC", "NSW", "QLD", "WA", "SA", "TAS", "ACT", "NT"].indexOf(name) + 1 || 99, time: (name) => TIME_BUCKETS.findIndex(([value]) => value === name) }[dimension]
    const defaultOrder = (a, b) => naturalOrder ? naturalOrder(a[0]) - naturalOrder(b[0]) : (tier(a[1]) - tier(b[1])) || (b[1].atRisk - a[1].atRisk) || (b[1].count - a[1].count)
    const compareGroups = [...groups.entries()].map(([name, members]) => [name, metrics(members)]).sort((a, b) => this.compareSort.key === "default" ? defaultOrder(a, b) || a[0].localeCompare(b[0], undefined, { numeric: true }) : this.compareSort.key === "label" ? a[0].localeCompare(b[0], undefined, { numeric: true }) * this.compareSort.direction : (sortValue(a[1]) - sortValue(b[1])) * this.compareSort.direction || a[0].localeCompare(b[0], undefined, { numeric: true }))
    const rowsToRender = [buildRow("Everyone in view", overall, true), ...compareGroups.map(([name, data]) => buildRow(name, data))]
    this.compareBodyTarget.replaceChildren(...rowsToRender)
  }

  selectCompareRow(row) {
    const dimension = row.dataset.compareDimension
    const value = row.dataset.compareValue
    if (["year", "state"].includes(dimension)) {
      const group = this.element.querySelector(`[data-filter-group="${dimension}"]`)
      if (!group.querySelector(`[data-filter-value="${CSS.escape(value)}"]`)) return
      this.setChipSelection(group, [value])
      this.applyFilters()
    } else {
      const dropdown = this.element.querySelector(`[data-dropdown-filter="${dimension}"]`)
      if (!dropdown) return
      dropdown.querySelectorAll("[data-dropdown-option]").forEach((option) => { option.checked = option.value === value })
      this.selectedFilters[dimension] = [value]
      this.refreshDropdownLabel(dropdown)
      this.applyFilters()
    }
  }

  renderPage() {
    const rows = [...this.studentsBodyTarget.querySelectorAll("[data-student-row]")]
    const filtered = (this.currentRows || rows).slice().sort((a, b) => this.compareStudentRows(a, b))
    this.element.querySelectorAll("[data-student-sort]").forEach((button) => {
      const active = button.dataset.studentSort === this.studentSort.key
      button.setAttribute("aria-sort", active ? (this.studentSort.direction === 1 ? "ascending" : "descending") : "none")
    })
    const start = (this.currentPage - 1) * 25
    this.studentsBodyTarget.append(...filtered)
    rows.forEach((row) => { const index = filtered.indexOf(row); row.hidden = index < 0 || index < start || index >= start + 25 })
    const end = Math.min(start + 25, filtered.length)
    this.paginationTarget.textContent = !filtered.length ? "No students found" : filtered.length <= 25 ? `Showing ${filtered.length} of ${filtered.length}` : `Showing ${start + 1} to ${end} of ${filtered.length}`
    const pageCount = Math.max(1, Math.ceil(filtered.length / 25))
    this.pageNumberTarget.textContent = `Page ${this.currentPage} of ${pageCount}`
    this.element.querySelector('[data-page="prev"]').disabled = this.currentPage <= 1
    this.element.querySelector('[data-page="next"]').disabled = this.currentPage >= pageCount
  }

  reset() {
    this.closeDropdowns()
    MULTI_CHIP_GROUPS.forEach((name) => {
      const group = this.element.querySelector(`[data-filter-group="${name}"]`)
      if (group) this.setChipSelection(group, [])
    })
    this.element.querySelectorAll("[data-dropdown-filter]").forEach((dropdown) => {
      dropdown.querySelectorAll("[data-dropdown-option]").forEach((option) => { option.checked = false })
      const search = dropdown.querySelector("[data-dropdown-search]")
      if (search) search.value = ""
      dropdown.querySelectorAll(".select-option").forEach((option) => { option.hidden = false })
      this.selectedFilters[dropdown.dataset.dropdownFilter] = []
      this.refreshDropdownLabel(dropdown)
    })
    this.element.querySelector("[data-student-search]").value = ""
    this.selectedStatus = "all"
    this.selectedReason = null
    this.studentSort = { key: "status", direction: 1 }
    this.syncStatusTabs()
    this.applyFilters()
  }

  renderNextStep(row, target, windowWeeks) {
    const name = row.querySelector(".student-name b")?.textContent || "The student"
    const team = row.dataset.team && row.dataset.team !== "Unassigned" ? row.dataset.team : "Team leader"
    const coach = row.dataset.coach && row.dataset.coach !== "Unassigned" ? row.dataset.coach : "Weekly coach"
    const subject = row.dataset.subject?.split(", ")[0] || "subject"
    const summary = this.windowSummary(row, windowWeeks)
    const booked = (summary.booked / Math.max(summary.weeks, 1)).toFixed(1)
    const attended = (summary.attended / Math.max(summary.weeks, 1)).toFixed(1)
    const onTarget = summary.indices.filter((index) => Number(row.dataset.weeks.split(",")[index]) >= target).length
    const nextCall = row.dataset.nextWeeklyCall ? ` (${row.dataset.nextWeeklyCall})` : " (time to be confirmed)"
    const reason = row.dataset.reason
    let title, owner, body
    if (row.dataset.quiet === "true") {
      title = "Gone quiet"
      owner = `${team}, within 2 business days`
      body = `No calls for 2 program weeks. Phone a parent rather than emailing. Open with ${name}'s goal, ask what has changed, and rebook the weekly call and one ${subject} call while you are on the phone.`
    } else if (row.dataset.status === "risk") {
      title = `At risk: ${reason === "underbooking" ? "under-booking" : "no-shows"}`
      owner = reason === "underbooking" ? `${team} and ${coach}, in the next weekly call` : `Customer service and ${coach}`
      body = reason === "underbooking"
        ? `${name} books ${booked} calls a week. In the next weekly call${nextCall}, book a standing ${subject} call before hanging up, tied to the next test or assignment.`
        : `${name} books ${booked} calls a week but attends ${attended}. Turn on 24-hour and 1-hour reminders to ${name} and a parent, rebook missed calls the same day, and check whether the times clash with sport or work.`
    } else if (row.dataset.status === "watch") {
      title = `Watch: ${reason === "underbooking" ? "under-booking" : "no-shows"}`
      owner = coach
      const context = `On target in ${onTarget} of ${summary.weeks} program weeks. `
      body = context + (reason === "underbooking"
        ? `Name the booking pattern in the next weekly call${nextCall} and book a standing ${subject} call to reach the target of ${target} calls a week. No parent call yet.`
        : `Name the missed-call pattern in the next weekly call${nextCall}, check the call times, agree on reminders, and rebook missed calls. No parent call yet.`)
    } else if (row.dataset.status === "track") {
      title = "On track"
      owner = coach
      body = `On target in ${onTarget} of ${summary.weeks} program weeks. Mention the streak in the next weekly call${nextCall}.`
    } else {
      const hasCalls = row.dataset.weeks.split(",").some((value) => Number(value) > 0)
      title = hasCalls ? "New" : "New, no calls yet"
      owner = hasCalls ? coach : "Customer service, today"
      body = hasCalls
        ? `Welcome ${name} in the next weekly call${nextCall}, confirm their goal, and agree on a regular weekly call and ${subject} call schedule to reach ${target} calls a week.`
        : `Call a parent today and book ${name}'s weekly call and a first ${subject} call, so the coming week has at least ${Math.max(2, target)} calls.`
    }
    this.drawerStatusTarget.textContent = `Next step: ${title}`
    this.element.querySelector("[data-next-step-owner]").textContent = `Owner: ${owner}`
    this.element.querySelector("[data-next-step-body]").textContent = body
    const card = this.drawerStatusTarget.closest(".next-step-card")
    card.style.borderLeftColor = `var(--${row.dataset.status === "track" ? "theme-track-color" : row.dataset.status === "watch" ? "theme-watch" : row.dataset.status === "new" ? "theme-new" : "theme-risk"})`
  }

  openDrawer(event) {
    const row = event.target.closest("[data-student-row]")
    if (!row) return
    this.drawerNameTarget.textContent = row.querySelector(".student-name b")?.textContent || "Student"
    const statusLabel = row.dataset.quiet === "true" ? "Gone quiet" : LABELS[row.dataset.status]
    const reasonLabels = { underbooking: "Under-booking", no_shows: "No-shows", gone_quiet: "No calls in the last 2 program weeks" }
    this.drawerStatusTarget.textContent = statusLabel
    this.drawerStatusChipTarget.textContent = statusLabel
    this.drawerStatusChipTarget.className = `status-pill status-${row.dataset.quiet === "true" ? "risk" : row.dataset.status}`
    this.drawerReasonTarget.textContent = reasonLabels[row.dataset.reason] || ""
    this.drawerReasonTarget.hidden = !this.drawerReasonTarget.textContent
    const weeks = row.dataset.weeks.split(",").map(Number)
    const booked = row.dataset.bookedWeeks.split(",").map(Number)
    const labels = row.dataset.weekLabels.split("|")
    const target = Number(this.element.querySelector('[data-filter-group="target"] .is-selected')?.dataset.filterValue || 2)
    const windowWeeks = Number(this.element.querySelector('[data-filter-group="weeks"] .is-selected')?.dataset.filterValue || 4)
    this.drawerIdTarget.textContent = `Status based on the last ${windowWeeks} program weeks`
    this.renderNextStep(row, target, windowWeeks)
    const flags = this.weekFlags(row)
    const holidays = flags.map((flag) => flag.started && flag.holiday)
    const notStarted = flags.map((flag) => !flag.started)
    const typeSeries = JSON.parse(row.dataset.typeSeries || "{}")
    const callTypes = [["weekly_call", "Weekly", "drawer-chart-weekly"], ["subject_call", "Subject", "drawer-chart-subject"], ["strategy_call", "Strategy", "drawer-chart-strategy"]]
    const attendedByType = callTypes.map(([type]) => typeSeries[type]?.attended || Array(weeks.length).fill(0))
    // Leave headroom above the tallest booked/attended column so its outline
    // remains visible. Holiday weeks share one background and no call columns.
    const peak = Math.max(target, ...booked, ...weeks)
    const bookedMax = Math.max(3, Math.ceil(peak) + 1)
    const chartWidth = 520, chartHeight = 210, chartLeft = 32, chartRight = 8, chartTop = 12, chartBottom = 26
    const plotWidth = chartWidth - chartLeft - chartRight, plotHeight = chartHeight - chartTop - chartBottom
    const plotBase = chartTop + plotHeight
    const chartY = (value) => chartTop + (1 - value / bookedMax) * plotHeight
    const slotWidth = plotWidth / weeks.length, barWidth = Math.min(30, slotWidth * 0.72)
    const tickStep = bookedMax > 4 ? Math.ceil(bookedMax / 4) : 1
    const chartGrid = Array.from({ length: bookedMax + 1 }, (_, value) => `<line class="drawer-chart-grid ${value === 0 ? "is-base" : ""}" x1="${chartLeft}" x2="${chartWidth - chartRight}" y1="${chartY(value)}" y2="${chartY(value)}"/>${value % tickStep === 0 ? `<text class="drawer-chart-axis" x="${chartLeft - 9}" y="${chartY(value) + 4}" text-anchor="end">${value}</text>` : ""}`).join("")
    const holidayRanges = []
    let holidayStart = null
    holidays.forEach((holiday, index) => {
      if (holiday && holidayStart === null) holidayStart = index
      if (holidayStart !== null && (!holiday || index === weeks.length - 1)) {
        const end = holiday ? index + 1 : index
        holidayRanges.push(`<rect class="drawer-chart-holiday" x="${chartLeft + holidayStart * slotWidth}" y="${chartTop}" width="${(end - holidayStart) * slotWidth}" height="${plotHeight}"/>`)
        holidayStart = null
      }
    })
    const chartBars = weeks.map((count, index) => {
      const center = chartLeft + (index + 0.5) * slotWidth
      const x = center - barWidth / 2
      let stacked = 0
      const segments = holidays[index] || notStarted[index] ? "" : callTypes.map(([, , className], typeIndex) => {
        const value = Number(attendedByType[typeIndex][index] || 0)
        if (!value) return ""
        const y = chartY(stacked + value)
        const height = value / bookedMax * plotHeight
        stacked += value
        return `<rect class="${className}" x="${x}" y="${y}" width="${barWidth}" height="${height}"/>`
      }).join("")
      const bookedOutline = !holidays[index] && !notStarted[index] && booked[index] > 0 ? `<rect class="drawer-chart-bar-booked" x="${x}" y="${chartY(booked[index])}" width="${barWidth}" height="${plotBase - chartY(booked[index])}" rx="2"/>` : ""
      const tip = notStarted[index] ? `${labels[index]}: not enrolled yet` : `${labels[index]}: ${booked[index]} booked, ${count} attended${holidays[index] ? "; school holidays" : ""}`
      const label = (weeks.length - 1 - index) % 2 === 0 ? `<text class="drawer-chart-date" x="${center}" y="${plotBase + 19}" text-anchor="middle">${labels[index]}</text>` : ""
      return `${segments}${bookedOutline}${label}<rect class="drawer-chart-hit" x="${chartLeft + index * slotWidth}" y="${chartTop}" width="${slotWidth}" height="${plotHeight}" data-tip="${tip}"/>`
    }).join("")
    const targetLine = `<line class="drawer-chart-target" x1="${chartLeft}" x2="${chartWidth - chartRight}" y1="${chartY(target)}" y2="${chartY(target)}"/>`
    this.drawerChartTarget.innerHTML = `<div class="drawer-chart-legend"><span><i class="drawer-chart-weekly"></i>Weekly</span><span><i class="drawer-chart-subject"></i>Subject</span><span><i class="drawer-chart-strategy"></i>Strategy</span><span><i class="drawer-chart-booked-key"></i>Booked</span><span><i class="drawer-chart-target-key"></i>Target ${target}</span></div><svg class="drawer-chart-svg" viewBox="0 0 ${chartWidth} ${chartHeight}" role="img" aria-label="Student calls booked and attended over the last 12 weeks">${chartGrid}<defs><pattern id="drawer-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="8" height="8" class="chart-hatch-paper"/><line x1="0" y1="0" x2="0" y2="8" class="chart-hatch-line" stroke-width="1.2"/></pattern></defs>${holidayRanges.join("")}${chartBars}${targetLine}</svg>`
    const summary = this.windowSummary(row, windowWeeks)
    const onTarget = summary.indices.filter((index) => weeks[index] >= target).length
    this.drawerOnTargetTarget.textContent = `${onTarget} of ${summary.weeks} program weeks`
    this.drawerShowRateTarget.textContent = `${Math.round(summary.booked ? summary.attended / summary.booked * 100 : 0)}%`
    this.drawerAttendedTarget.textContent = (summary.attended / Math.max(summary.weeks, 1)).toFixed(2)
    this.drawerBookedTarget.textContent = (summary.booked / Math.max(summary.weeks, 1)).toFixed(2)
    this.drawerCorrectionsTarget.textContent = String(summary.corrections)
    this.drawerAiCorrectionsTarget.textContent = String(summary.aiCorrections)
    this.drawerLastCallTarget.textContent = row.dataset.lastCall || "—"
    const escape = (value) => value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]))
    this.drawerDetailsTarget.innerHTML = `<div><dt>Year level</dt><dd>${escape(row.dataset.year)}</dd></div><div><dt>State</dt><dd>${escape(row.dataset.state)}</dd></div><div><dt>Weekly coach</dt><dd>${escape(row.dataset.coach)}</dd></div><div><dt>Subject coach</dt><dd>${escape(row.dataset.subjectCoachLabel)}</dd></div><div><dt>Team leader</dt><dd>${escape(row.dataset.team)}</dd></div><div><dt>Time in program</dt><dd>${TIME_BUCKETS.find(([value]) => value === this.timeBucket(row))?.[1] || "—"}</dd></div>`
    this.drawerTarget.classList.add("is-open")
    this.drawerTarget.setAttribute("aria-hidden", "false")
    this.backdropTarget.classList.add("is-open")
  }

  closeDrawer() {
    this.drawerTarget.classList.remove("is-open")
    this.drawerTarget.setAttribute("aria-hidden", "true")
    this.backdropTarget.classList.remove("is-open")
  }
}
